import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  useListAnnuityStatementsQuery,
  useListAnnuityPoliciesQuery,
  useUploadAnnuityStatementMutation,
  useGetAnnuityStatementUrlMutation,
  useDeleteAnnuityStatementMutation,
  useGetAllianzStatusQuery,
  useSyncAllianzMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const ACCEPT = '.pdf';
const MAX_MB = 25;

// Map the backend textract_status vocabulary to a colored chip. `running`
// and `pending` mean "still working" — the page re-polls while any row is in
// one of those states so the analysis fills in without a manual refresh.
const STATUS_META = {
  done:     { label: 'Analyzed',   bg: brand.green,     working: false },
  running:  { label: 'Analyzing…', bg: brand.tealBlue,  working: true  },
  pending:  { label: 'Queued',     bg: brand.charcoal,  working: true  },
  deferred: { label: 'Queued',     bg: brand.charcoal,  working: true  },
  failed:   { label: 'Failed',     bg: brand.red,       working: false },
  skipped:  { label: 'Skipped',    bg: brand.charcoal,  working: false },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status || 'Unknown', bg: brand.charcoal };
  return (
    <span style={{
      background: meta.bg, color: 'white', fontSize: '0.7rem', fontWeight: 700,
      padding: '0.15rem 0.55rem', borderRadius: '999px', whiteSpace: 'nowrap',
    }}>
      {meta.label}
    </span>
  );
}

StatusChip.propTypes = {
  status: PropTypes.string,
};

function fmtBytes(n) {
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Values are stored as text exactly as the statement printed them (e.g.
// "$12,400.00"), so just render them verbatim with an em dash for blanks.
function Cell({ value, align }) {
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: '#b9c3cd', fontWeight: 400 }}>—</span>;
  }
  return <span style={align === 'end' ? { fontWeight: 600, color: brand.deepBlue } : undefined}>{value}</span>;
}

Cell.propTypes = {
  value: PropTypes.string,
  align: PropTypes.string,
};

export default function Annuity() {
  const { data: statements = [], refetch, isLoading } = useListAnnuityStatementsQuery(undefined, {
    // Re-poll every 5s; cheap, and the only way the async PDF path surfaces
    // its result. RTK stops re-rendering when the data stops changing.
    pollingInterval: 5000,
  });
  // The per-policy ledger lives in its own table (annuity_policies), populated
  // from each statement's tables on upload. Poll alongside the statements so a
  // newly-analyzed statement's products fill in without a manual refresh.
  const { data: policies = [], refetch: refetchPolicies } = useListAnnuityPoliciesQuery(undefined, {
    pollingInterval: 5000,
  });
  const [uploadStatement] = useUploadAnnuityStatementMutation();
  const [getUrl] = useGetAnnuityStatementUrlMutation();
  const [deleteStatement] = useDeleteAnnuityStatementMutation();

  // Allianz commission feed — pulls straight into the same ledger.
  const { data: allianzStatus } = useGetAllianzStatusQuery();
  const [syncAllianz, { isLoading: syncing }] = useSyncAllianzMutation();
  const [syncResult, setSyncResult] = useState(null);

  const onSyncAllianz = useCallback(async () => {
    setError(null);
    setSyncResult(null);
    try {
      const res = await syncAllianz({ body: { days: 90 } }).unwrap();
      setSyncResult(res);
      await refetchPolicies();
    } catch (err) {
      setError(err?.data?.detail || 'Allianz sync failed.');
    }
  }, [syncAllianz, refetchPolicies]);

  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const fileInputRef = useRef(null);

  const uploadFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    let dupes = 0, added = 0;
    for (const file of files) {
      if (file.size > MAX_MB * 1024 * 1024) {
        setError(`${file.name} exceeds the ${MAX_MB} MB limit.`);
        continue;
      }
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await uploadStatement({ body: formData }).unwrap();
        if (res?.duplicate) dupes += 1;
        else added += 1;
      } catch (err) {
        setError(err?.data?.detail || `Upload failed for ${file.name}.`);
      }
    }
    setBusy(false);
    const parts = [];
    if (added) parts.push(`${added} statement${added > 1 ? 's' : ''} uploaded`);
    if (dupes) parts.push(`${dupes} duplicate${dupes > 1 ? 's' : ''} skipped`);
    if (parts.length) setNotice(parts.join(' · '));
    await refetch();
    await refetchPolicies();
  }, [uploadStatement, refetch, refetchPolicies]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const onDownload = async (id) => {
    try {
      const { url } = await getUrl({ id }).unwrap();
      if (url) window.open(url, '_blank', 'noopener');
    } catch {
      setError('Could not open that statement.');
    }
  };

  const onDelete = async (id, label) => {
    if (!window.confirm(`Remove "${label || 'this statement'}"? This deletes the file and its analysis.`)) return;
    try {
      await deleteStatement({ id }).unwrap();
      await refetch();
      await refetchPolicies();
    } catch {
      setError('Could not delete that statement.');
    }
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.25rem' }}>
        <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>Annuity Statements</h4>
        <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>
          Upload annuity statements — each is read automatically with AWS Textract and analyzed below.
        </p>
      </div>

      {/* ── Drag & drop upload zone ───────────────────────────────────────── */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
        style={{
          border: `2px dashed ${dragging ? brand.tealBlue : '#b9c3cd'}`,
          background: dragging ? 'rgba(0,105,166,0.06)' : 'white',
          borderRadius: '12px',
          padding: '2.25rem 1.5rem',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          marginBottom: '1.25rem',
        }}
      >
        <i className="bi bi-cloud-arrow-up" style={{ fontSize: '2.25rem', color: brand.tealBlue }} />
        <div style={{ fontWeight: 700, color: brand.deepBlue, marginTop: '0.5rem' }}>
          {busy ? 'Uploading…' : 'Drag & drop annuity statements here'}
        </div>
        <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginTop: '0.25rem' }}>
          or <span style={{ color: brand.tealBlue, textDecoration: 'underline' }}>browse files</span>
          {' '}· PDF · up to {MAX_MB} MB each
        </div>
        {busy && <div className="spinner-border spinner-border-sm text-primary mt-3" role="status" />}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          style={{ display: 'none' }}
          onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
        />
      </div>

      {notice && (
        <div style={{ marginBottom: '1rem', color: brand.green, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <i className="bi bi-check-circle" /> {notice}
        </div>
      )}
      {error && (
        <div style={{ marginBottom: '1rem', color: brand.red, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <i className="bi bi-exclamation-circle" /> {error}
        </div>
      )}

      {/* ── Allianz commission feed ───────────────────────────────────────── */}
      <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', marginBottom: '1.25rem' }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h6 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>
              Allianz commission feed
            </h6>
            <div style={{ color: brand.charcoal, fontSize: '0.8rem', marginTop: '0.25rem' }}>
              {allianzStatus?.configured
                ? 'Pull the last 90 days of Allianz commissions straight into the Products ledger below. Safe to run repeatedly — only new lines are added.'
                : 'Not configured on this server. Add the Allianz API credentials to enable the live feed.'}
            </div>
            {syncResult && (
              <div style={{ color: brand.green, fontSize: '0.8rem', marginTop: '0.4rem' }}>
                <i className="bi bi-check-circle" />{' '}
                {syncResult.added} added · {syncResult.skipped} already had ·{' '}
                {syncResult.range?.start}–{syncResult.range?.end}
                {syncResult.errors?.length ? ` · ${syncResult.errors.length} window error(s)` : ''}
              </div>
            )}
          </div>
          <button
            className="btn btn-sm"
            style={{ background: brand.tealBlue, color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}
            disabled={!allianzStatus?.configured || syncing}
            onClick={onSyncAllianz}
          >
            {syncing
              ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Syncing…</>
              : <><i className="bi bi-arrow-repeat me-1" />Sync now</>}
          </button>
        </div>
      </div>

      {/* ── Analysis grid ─────────────────────────────────────────────────── */}
      <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px' }}>
        <div className="card-body">
          <h6 style={{ color: brand.deepBlue, fontWeight: 800, marginBottom: '1rem' }}>
            Uploaded statements ({statements.length})
          </h6>

          {isLoading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" role="status" /></div>
          ) : statements.length === 0 ? (
            <div style={{ color: brand.charcoal, fontSize: '0.875rem', padding: '1rem 0' }}>
              No statements yet. Drop one above to get started.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ color: brand.charcoal, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.03em' }}>
                    <th>Statement</th>
                    <th>Institution</th>
                    <th>Account</th>
                    <th>Type</th>
                    <th>Period</th>
                    <th className="text-end">Balance</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((s) => {
                    const failed = s.textract_status === 'failed';
                    const errMsg = failed && s.textract_extract_json && s.textract_extract_json.error;
                    return (
                      <tr key={s.id}>
                        <td>
                          <div style={{ fontWeight: 600, color: brand.deepBlue }}>
                            {s.display_label || s.original_filename || `Statement #${s.id}`}
                          </div>
                          <div style={{ color: brand.charcoal, fontSize: '0.75rem' }}>
                            {s.original_filename}{s.file_size_bytes ? ` · ${fmtBytes(s.file_size_bytes)}` : ''}
                          </div>
                          {errMsg && (
                            <div style={{ color: brand.red, fontSize: '0.72rem', marginTop: '0.15rem' }}>{errMsg}</div>
                          )}
                        </td>
                        <td>{s.institution || <span style={{ color: '#b9c3cd' }}>—</span>}</td>
                        <td>{s.account_number_last4 ? `…${s.account_number_last4}` : <span style={{ color: '#b9c3cd' }}>—</span>}</td>
                        <td>{s.account_type || <span style={{ color: '#b9c3cd' }}>—</span>}</td>
                        <td>{s.statement_period || s.statement_date || <span style={{ color: '#b9c3cd' }}>—</span>}</td>
                        <td className="text-end" style={{ fontWeight: 700, color: brand.deepBlue }}>
                          {s.total_balance || <span style={{ color: '#b9c3cd', fontWeight: 400 }}>—</span>}
                        </td>
                        <td><StatusChip status={s.textract_status} /></td>
                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                          <button
                            className="btn btn-sm btn-link p-0 me-2"
                            style={{ color: brand.tealBlue }}
                            title="Download"
                            onClick={() => onDownload(s.id)}
                          >
                            <i className="bi bi-download" />
                          </button>
                          <button
                            className="btn btn-sm btn-link p-0"
                            style={{ color: brand.red }}
                            title="Delete"
                            onClick={() => onDelete(s.id, s.display_label || s.original_filename)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Products / policy lines ────────────────────────────────────────── */}
      {/* One row per individual product extracted from the uploaded statements'
          tables (the annuity_policies ledger). Carrier, client, and statement
          date fall back to the parent statement's summary when a line didn't
          carry its own. */}
      <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', marginTop: '1.25rem' }}>
        <div className="card-body">
          <h6 style={{ color: brand.deepBlue, fontWeight: 800, marginBottom: '1rem' }}>
            Products ({policies.length})
          </h6>

          {policies.length === 0 ? (
            <div style={{ color: brand.charcoal, fontSize: '0.875rem', padding: '1rem 0' }}>
              No products extracted yet. Once a statement finishes analyzing, its individual products appear here.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ color: brand.charcoal, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.03em' }}>
                    <th>Product</th>
                    <th>Carrier</th>
                    <th>Client</th>
                    <th>Policy #</th>
                    <th>Statement date</th>
                    <th className="text-end">Premium</th>
                    <th className="text-end">Gross comm.</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map((p) => (
                    <tr key={p.id}>
                      <td><Cell value={p.product} /></td>
                      <td><Cell value={p.carrier_name} /></td>
                      <td><Cell value={p.client_name} /></td>
                      <td><Cell value={p.policy_number} /></td>
                      <td><Cell value={p.statement_date} /></td>
                      <td className="text-end"><Cell value={p.premium} align="end" /></td>
                      <td className="text-end"><Cell value={p.gross_commission} align="end" /></td>
                      <td style={{ color: brand.charcoal, fontSize: '0.75rem' }}>
                        {p.source_filename || p.data_source || <span style={{ color: '#b9c3cd' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
